import { useEffect, useMemo, useState } from 'react';

import { fetchBanWords } from './fetchBanWords';
import { filterBanWords as filterBanWordsFromText } from './filterBanWords';
import { hasBanWords as hasBanWordsInText } from './hasBanWords';
import type { BanWordsData } from './utils';
import { createRegexPatterns } from './utils';

const emptyData: BanWordsData = {
  blackList: [],
  whiteList: [],
  isolated: [],
  inner: [],
};

type UseZepBanWordsOptions = {
  enableFilterBadWords?: boolean;
  additionalBlackList?: string[];
};

export const useZepBanWords = ({
  enableFilterBadWords = true,
  additionalBlackList,
}: UseZepBanWordsOptions = {}) => {
  const [banWordsData, setBanWordsData] = useState<BanWordsData>(emptyData);
  const [isLoading, setIsLoading] = useState(enableFilterBadWords);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enableFilterBadWords) return;

    fetchBanWords()
      .then(setBanWordsData)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [enableFilterBadWords]);

  const patterns = useMemo(() => {
    if (!enableFilterBadWords) {
      return {
        whiteListRegex: null,
        blackListRegex: null,
        innerRegex: null,
        isolatedRegex: null,
      };
    }
    return createRegexPatterns(banWordsData, additionalBlackList);
  }, [banWordsData, enableFilterBadWords, additionalBlackList]);

  return {
    filterBanWords: (message: string) =>
      filterBanWordsFromText(message, patterns),
    hasBanWords: (message: string) => hasBanWordsInText(message, patterns),
    isLoading,
    error,
  };
};
